import React, { useState, useEffect } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  Switch,
  TouchableOpacity,
  Platform,
} from "react-native";

import moment from "moment";

import appStyles from "../../../appStyles";
import Header from "../../common/header/index";
import ManaButton from "../../common/ManaButton";
import AppConfig from "../../../../config/AppConfig";
import TimePickerDialog from "../../common/TimePickerDialog";
import DatePickerDialog from "../../common/DatePickerDialog";
import RoundCheckbox from "../../common/RoundCheckbox";
import OnboardingConfirmDialog from "../../onboarding/OnboardingConfirmDialog";
import TimezoneDialog from "../../common/TimezoneDialog";
import { convertAMPMTo24h } from "../../../../helpers/utils";
import AppTheme from "../../AppTheme";

const DATE_FORMAT = "YYYY-MM-DD";

const thumbColor = "#FFF";

export default function EditUser({ controller, user, goBack }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [userModel, setUserModel] = useState(user);
  const [churnedText, setChurnedText] = useState("");
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openUnconfirmConfirmation, setOpenUnconfirmConfirmation] = useState(
    false,
  );
  const [isTimezoneDialogOpen, setIsTimezoneDialogOpen] = useState(false);
  const [useDeviceTz, setUseDeviceTz] = useState(
    userModel.tz_override == null ||
      userModel.tz_override === "" ||
      "Current" === userModel.tz_override,
  );

  const use24hFormat = false;

  const { show_guide_tools } = controller.state?.settings || {};
  const disableGuidesReset = false; //show_guide_tools && user.next_to_log;

  useEffect(() => {
    const isChurned = user.churned == "1";
    setUserModel({ ...user, isChurned });
    setChurnedText(isChurned ? "Unchurned ?" : "Churned ?");

    const dateModel = moment();
    setDate(dateModel.format(DATE_FORMAT));
    setTime(dateModel.format("h:mm a"));
  }, []);

  const updateUser = (updates) => {
    let model = { ...updates, email: userModel.email };

    if (updates.clone) {
      model.cloneEmail = `clone${moment().format("YYYYMMDD")}-${
        userModel.email
      }`;
    }
    if (updates.reset || updates.unconfirm) {
      model = { ...model, date, time };
    }

    model.time = convertAMPMTo24h(time);

    const action = {
      action: "save",
      target: "user",
      ...model,
    };

    setIsLoading(true);
    console.log("Save User");
    controller.cardActionWithCallback(action, (data) => {
      setIsLoading(false);

      // After ‘Unlock’, go to enabled user detail, not home
      if (updates.unlock) {
        setUserModel({ ...userModel, enabled: 1 });
      } else if (
        !updates.hasOwnProperty("churned") &&
        !updates.hasOwnProperty("has_scheduled_call")
      ) {
        goBack();
      }
    });
  };

  const impersonateUser = () => {
    const email = userModel.email;
    controller.apiService.setImpersonate(email);
    controller.resetState(true, email);
    controller.getItemsWithStats("feed", "rn:home_card");
  };

  const updateDate = (newDate) => {
    setDate(newDate);
    setIsDatePickerOpen(false);
  };

  const resetUser = () => {
    setOpenConfirmation(false);
    updateUser({ reset: true });
  };

  const unconfirmForUser = () => {
    setOpenUnconfirmConfirmation(false);
    updateUser({ unconfirm: true });
  };

  const toggleChurnedState = (value) => {
    setUserModel({ ...userModel, isChurned: value });
    updateUser({ churned: value });
  };

  const toggleScheduledCallState = (value) => {
    setUserModel({ ...userModel, has_scheduled_call: value });
    updateUser({ has_scheduled_call: value });
  };

  const toggleMarketingEmailState = (value) => {
    setUserModel({ ...userModel, marketing_email_flag: value });
    updateUser({ marketing_email_flag: value });
  };

  const updateSettings = (key, value) => {
    if (!key) return;
    userModel[key] = value;

    let targetKey = key,
      targetValue = value;
    if (!targetKey) return;

    const action = {
      action: "save",
      target: "preferences",
      target_email: userModel.email,
      [targetKey]: targetValue,
    };
    controller.cardAction(action);
  };

  if (!user) return null;

  const hasUnconfirm = userModel.email && userModel.email.includes("clone");

  return (
    <View style={appStyles.container}>
      <OnboardingConfirmDialog
        isOpen={openConfirmation}
        title="Are you sure?"
        description="Tapping confirm will erase all tracked data before the selected date. PLEASE BE VERY CAREFUL, THERE IS NO UNDO FOR THIS ACTION. As a protection, the operation will fail if there are more than 2 days logged"
        onConfirm={resetUser}
        onClose={() => setOpenConfirmation(false)}
      />

      <OnboardingConfirmDialog
        isOpen={openUnconfirmConfirmation}
        title="Are you sure?"
        description="Tapping confirm will erase all tracked data after the selected time. PLEASE BE VERY CAREFUL, THERE IS NO UNDO FOR THIS ACTION."
        onConfirm={unconfirmForUser}
        onClose={() => setOpenUnconfirmConfirmation(false)}
      />

      <TimePickerDialog
        value={time}
        isOpen={isTimePickerOpen}
        use24hFormat={use24hFormat}
        minutesInterval={controller.state.settings?.interval_duration}
        onConfirm={(value) => setTime(moment(value).format("h:mm A"))}
        onClose={() => setIsTimePickerOpen(false)}
      />

      <DatePickerDialog
        value={time}
        controller={controller}
        isOpen={isDatePickerOpen}
        onConfirm={(value) => updateDate(value)}
        onClose={() => setIsDatePickerOpen(false)}
      />

      <TimezoneDialog
        onConfirm={(value) => updateSettings("tz_override", value)}
        value={userModel.tz_override}
        isOpen={isTimezoneDialogOpen}
        onClose={() => setIsTimezoneDialogOpen(false)}
      />

      <Header
        title={userModel.email}
        hideAvatar
        goBack={goBack}
        controller={controller}
        isLoading={isLoading}
        headerTitleStyle={styles.headerText}
      />

      <ScrollView
        style={[
          appStyles.scrollView,
          { backgroundColor: "#F3F2F1" },
        ]}
      >
        {userModel.enabled == "0" ? (
          <>
            <View style={[appStyles.verticalButtons, { marginTop: 55 }]}>
              <ManaButton
                isMainButton
                onPress={() => updateUser({ unlock: true })}
                style={styles.button}
                text="Unlock"
              />
            </View>

            <View style={styles.row}>
              <Text style={styles.optionText}>{churnedText}</Text>
              <TouchableOpacity style={styles.checkbox}>
                <RoundCheckbox
                  size={20}
                  checked={userModel.isChurned}
                  onValueChange={(value) => toggleChurnedState(value)}
                />
              </TouchableOpacity>
            </View>
          </>
        ) : (
          <View style={{ marginTop: 29 }}>
            <View style={styles.row}>
              <Text style={styles.text}>Date</Text>
              <Text
                style={styles.value}
                onPress={() => setIsDatePickerOpen(true)}
              >
                {date}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.text}>Time</Text>
              <Text
                style={styles.value}
                onPress={() => setIsTimePickerOpen(true)}
              >
                {time}
              </Text>
            </View>

            <View style={[appStyles.verticalButtons, { marginTop: 26 }]}>
              <ManaButton
                isMainButton
                disabled={disableGuidesReset}
                onPress={() => setOpenConfirmation(true)}
                style={styles.button}
                text="Reset"
              />

              {hasUnconfirm ? (
                <ManaButton
                  isMainButton
                  onPress={() => setOpenUnconfirmConfirmation(true)}
                  style={styles.button}
                  text="Unconfirm"
                />
              ) : null}

              <ManaButton
                onPress={() => updateUser({ clone: true })}
                style={styles.button}
                text="Clone"
              />

              <ManaButton
                onPress={() => impersonateUser()}
                style={styles.button}
                text="Impersonate"
              />
            </View>

            <View style={styles.row}>
              <Text style={styles.optionText}>{churnedText}</Text>
              <TouchableOpacity style={styles.checkbox}>
                <RoundCheckbox
                  size={20}
                  checked={userModel.isChurned}
                  onValueChange={(value) => toggleChurnedState(value)}
                />
              </TouchableOpacity>
            </View>

            <View style={styles.row}>
              <Text style={styles.optionText}>Guide Call Scheduled?</Text>
              <TouchableOpacity style={styles.checkbox}>
                <RoundCheckbox
                  size={20}
                  checked={!!+userModel.has_scheduled_call}
                  onValueChange={(value) => toggleScheduledCallState(value)}
                />
              </TouchableOpacity>
            </View>

            <View style={styles.row}>
              <Text style={styles.optionText}>Receive Marketing Emails?</Text>
              <TouchableOpacity style={styles.checkbox}>
                <RoundCheckbox
                  size={20}
                  checked={!!+userModel.marketing_email_flag}
                  onValueChange={(value) => toggleMarketingEmailState(value)}
                />
              </TouchableOpacity>
            </View>

            {/* Timezone */}
            <View style={styles.row}>
              <Text style={styles.optionText}>Use Device Timezone</Text>
              <Switch
                thumbColor={thumbColor}
                onValueChange={() => {
                  setUseDeviceTz(!useDeviceTz);
                  updateSettings(
                    "tz_override",
                    useDeviceTz
                      ? "Current"
                      : userModel.tz_override != null &&
                        userModel.tz_override !== "Current"
                      ? userModel.tz_override
                      : "America/Los_Angeles",
                  );
                }}
                value={useDeviceTz}
              />
            </View>

            {!useDeviceTz ? (
              <View style={styles.row}>
                <View>
                  <Text style={[styles.optionText, styles.timezone]}>
                    Time Zone
                  </Text>
                </View>
                <TouchableOpacity onPress={() => setIsTimezoneDialogOpen(true)}>
                  <Text style={[styles.optionValue]}>
                    {userModel.tz_override}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  headerText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
    color: "#FFF",
    maxWidth: "85%",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    height: 44,
  },
  button: {
    maxWidth: 200,
    height: 50,
    marginBottom: 16,
  },
  text: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    color: "#2D3142",
  },
  value: {
    color: AppTheme.DEFAULT_COLOR,
  },
  optionText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    textTransform: "capitalize",
    color: "#2D3142",
    flex: 1,
    marginLeft: 10,
  },
  checkbox: {
    marginRight: 5,
  },
  optionTextSmall: {
    fontSize: 12,
    lineHeight: 14,
  },
  timezone: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 12,
    lineHeight: 14,
    letterSpacing: 0.5,
  },
  optionValue: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.DEFAULT_COLOR,
    textAlign: "center",
  },
});
